/**
 * Analytics — GA4 + GTM klant-side, gated op env-vars (STA-15).
 *
 * Geen tracking-cookies of fingerprinting buiten wat Google's standaard scripts
 * doen. PostHog is bewust verwijderd (2026-04-29 directive Sam).
 *
 * Activatie:
 *   - `VITE_GA4_ID=G-XXXXXXXXXX`   → laadt gtag.js + initialiseert GA4
 *   - `VITE_GTM_ID=GTM-XXXXXXX`    → laadt gtm.js + push naar `dataLayer`
 *
 * Beide vars worden gevalideerd; placeholders / verkeerde formats worden
 * stilletjes geweigerd (geen broken script-tags in productie).
 *
 * API ongewijzigd: bestaande call-sites in App.tsx / routes blijven werken.
 */

// Props is bewust permissief — call-sites geven typed UserProfile / event-payloads
// door en we serializen alleen primitieve top-level keys (zie sanitizeProps).
type Props = Record<string, unknown> | object;

const GA4_ID_RE = /^G-[A-Z0-9]+$/;
const GTM_ID_RE = /^GTM-[A-Z0-9]+$/;
const PLACEHOLDER_RE = /^(REPLACE_ME|TODO|FIXME|XXX|YOUR_.*|null|undefined|\.\.\.)$/i;

function valid(s: string | undefined, pattern: RegExp): string | null {
  if (!s) return null;
  const v = s.trim();
  if (PLACEHOLDER_RE.test(v)) return null;
  return pattern.test(v) ? v : null;
}

function readEnv(): { ga4: string | null; gtm: string | null } {
  // In tests we kunnen via globalThis.__STAGERY_ANALYTICS_ENV een env-object injecteren
  // omdat vi.stubEnv niet altijd doorwerkt op import.meta.env voor custom VITE_ vars.
  const testOverride = (globalThis as { __STAGERY_ANALYTICS_ENV?: Record<string, string> })
    .__STAGERY_ANALYTICS_ENV;
  const env = testOverride
    ?? (import.meta as ImportMeta & { env?: Record<string, string> }).env
    ?? {};
  return {
    ga4: valid(env.VITE_GA4_ID, GA4_ID_RE),
    gtm: valid(env.VITE_GTM_ID, GTM_ID_RE),
  };
}

let _ids: { ga4: string | null; gtm: string | null } = { ga4: null, gtm: null };

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function loadScriptOnce(src: string, id: string): boolean {
  if (typeof document === 'undefined') return false;
  if (document.getElementById(id)) return true;
  const s = document.createElement('script');
  s.async = true;
  s.id = id;
  s.src = src;
  document.head.appendChild(s);
  return true;
}

function ensureDataLayer(): void {
  if (typeof window === 'undefined') return;
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }
}

let _initialized = false;

export const initAnalytics = (): void => {
  if (_initialized) return;
  _initialized = true;
  if (typeof window === 'undefined') return;

  _ids = readEnv();
  ensureDataLayer();

  // GTM — push the dataLayer init event then load gtm.js
  if (_ids.gtm) {
    window.dataLayer!.push({
      'gtm.start': Date.now(),
      event: 'gtm.js',
    });
    loadScriptOnce(`https://www.googletagmanager.com/gtm.js?id=${_ids.gtm}`, 'gtm-js');
  }

  // GA4 — gtag.js + config
  if (_ids.ga4) {
    loadScriptOnce(`https://www.googletagmanager.com/gtag/js?id=${_ids.ga4}`, 'ga4-js');
    const gtag = (...args: unknown[]) => {
      window.dataLayer!.push(args);
    };
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', _ids.ga4, {
      // SPA: we sturen pageviews handmatig vanuit de router
      send_page_view: false,
      anonymize_ip: true,
    });
  }
};

export const identifyUser = (email: string, properties: Props = {}): void => {
  if (!_initialized || typeof window === 'undefined') return;
  const userId = simpleHash(email);
  if (window.gtag && _ids.ga4) {
    window.gtag('config', _ids.ga4, { user_id: userId });
    window.gtag('set', 'user_properties', sanitizeProps(properties));
  }
  if (_ids.gtm) {
    window.dataLayer?.push({ event: 'identify', user_id: userId, ...sanitizeProps(properties) });
  }
};

export const updateUserProperties = (properties: Props): void => {
  if (!_initialized || typeof window === 'undefined') return;
  const clean = sanitizeProps(properties);
  if (window.gtag && _ids.ga4) {
    window.gtag('set', 'user_properties', clean);
  }
  if (_ids.gtm) {
    window.dataLayer?.push({ event: 'user_properties', ...clean });
  }
};

export const trackEvent = (eventName: string, properties: Props = {}): void => {
  if (!_initialized || typeof window === 'undefined') return;
  const clean = sanitizeProps(properties);
  if (window.gtag && _ids.ga4) {
    window.gtag('event', eventName, clean);
  }
  if (_ids.gtm) {
    window.dataLayer?.push({ event: eventName, ...clean });
  }
};

// --- helpers ---

function sanitizeProps(p: Props): Record<string, unknown> {
  // Strip top-level PII keys defensief.
  const blocked = new Set(['email', 'phone', 'password', 'token', 'jwt', 'auth']);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(p as Record<string, unknown>)) {
    if (blocked.has(k.toLowerCase())) continue;
    if (typeof v === 'object' && v !== null) {
      // Skip nested objects om PII-leakage te voorkomen
      continue;
    }
    out[k] = v;
  }
  return out;
}

function simpleHash(input: string): string {
  // Niet-cryptografisch, alleen om e-mail naar opaque token te zetten.
  // Voor echte privacy is server-side hashing met salt beter, maar dit
  // voorkomt PII in GA4-payloads.
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) + h) ^ input.charCodeAt(i);
  }
  return 'u_' + (h >>> 0).toString(36);
}

// Diagnostics — handig voor dev-console:
export const __analyticsDebug = () => ({
  initialized: _initialized,
  ga4: _ids.ga4,
  gtm: _ids.gtm,
});

// Test-only: reset internal state (vitest gebruikt dit niet via reset modules,
// maar exporteren is handig wanneer iemand handmatig wil her-initialiseren).
export const __resetAnalyticsForTest = () => {
  _initialized = false;
  _ids = { ga4: null, gtm: null };
};
